import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function JS_Where_To() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📍 JavaScript – Where To</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript can be placed in different parts of an HTML document. You can
        put it directly inside HTML tags, inside the <code>{`<head>`}</code>, or
        at the end of the <code>{`<body>`}</code> for better performance. You
        can also keep it in separate <b>.js</b> files and link them.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            JavaScript can be added <b>inline, internal, or external</b>.
          </li>
          <li>
            Best practice is to keep scripts in an <b>external .js file</b>.
          </li>
          <li>
            Place <code>{`<script>`}</code> tags at the <b>end of body</b> to
            avoid blocking page load.
          </li>
          <li>
            Inline JavaScript should be avoided — use event listeners instead.
          </li>
          <li>Reuse external scripts across multiple pages for efficiency.</li>
        </ul>
      </div>

      {/* Inline JS */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Inline JavaScript</h2>
        <p>
          JavaScript can be written directly inside an HTML element’s attribute,
          usually with <code>onclick</code>, <code>onmouseover</code>, etc.
        </p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <body>
    <button onclick="alert('Hello from Inline JS!')">
      Click Me
    </button>
  </body>
</html>`}
        />
        <div style={{ marginTop: "1rem" }}>
          <button
            onClick={() => alert("Hello from Inline JS!")}
            style={{
              padding: "10px 20px",
              background: "#16a34a",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Try Inline Example
          </button>
        </div>
      </div>

      {/* Internal JS */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Internal JavaScript</h2>
        <p>
          JavaScript can be placed inside the HTML file using the{" "}
          <code>{`<script>`}</code> tag.
        </p>
        <CodeCopyBlock
          code={`<!DOCTYPE html>
<html>
  <head>
    <script>
      function greet() {
        alert("Hello from Internal JS!");
      }
    </script>
  </head>
  <body>
    <button onclick="greet()">Click Me</button>
  </body>
</html>`}
        />
        <div style={{ marginTop: "1rem" }}>
          <button
            onClick={() => alert("Hello from Internal JS!")}
            style={{
              padding: "10px 20px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Try Internal Example
          </button>
        </div>
      </div>

      {/* External JS */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ External JavaScript</h2>
        <p>
          It’s best practice to keep JavaScript in a separate file (with{" "}
          <b>.js</b> extension) and link it with the <code>{`<script>`}</code>{" "}
          tag. This keeps code clean and reusable.
        </p>
        <CodeCopyBlock
          code={`<!-- index.html -->
<!DOCTYPE html>
<html>
  <head>
    <script src="script.js"></script>
  </head>
  <body>
    <button onclick="sayHello()">Click Me</button>
  </body>
</html>

<!-- script.js -->
function sayHello() {
  alert("Hello from External JS!");
}`}
        />
      </div>

      {/* Best Practice */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Best Practice</h2>
        <ul className="tutorial-list-points">
          <li>
            Place <code>{`<script>`}</code> tags at the <b>end of body</b> for
            better performance.
          </li>
          <li>
            Use <b>external JavaScript</b> files for reusability and cleaner
            code.
          </li>
          <li>Keep inline JS minimal — use event listeners in JS instead.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Syntax</b>
          </li>
          <li>
            Understand <b>Variables and Data Types</b>
          </li>
          <li>
            Explore <b>Operators</b> and <b>Functions</b>
          </li>
        </ul>
      </div>
    </div>
  );
}
